// src/components/CommandSearchBar.jsx
// Barra de busqueda global animada (estilo "action search bar"), conectada
// a la busqueda real de la app (SearchContext -> GET /search) en vez de una
// lista de acciones locales de ejemplo. Reemplaza el buscador simple que
// vivia dentro de Header.jsx.
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, HardDrive } from 'lucide-react';
import { Search, Send, User, Ticket as TicketIcon, X } from './icons';
import { useSearch } from '../contexts/SearchContext';

const TYPE_ICON = {
  activo: <HardDrive className="h-4 w-4 text-blue-500" />,
  usuario: <User className="h-4 w-4 text-violet-500" />,
  ticket: <TicketIcon className="h-4 w-4 text-orange-500" />,
};

const TYPE_LABEL = {
  activo: 'Activo',
  usuario: 'Usuario',
  ticket: 'Ticket',
};

const container = {
  hidden: { opacity: 0, height: 0 },
  show: {
    opacity: 1,
    height: 'auto',
    transition: { height: { duration: 0.35 }, staggerChildren: 0.05 },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { height: { duration: 0.25 }, opacity: { duration: 0.15 } },
  },
};

const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: -6, transition: { duration: 0.15 } },
};

function ResultRow({ icon, label, description, end, onClick }) {
  return (
    <motion.li
      variants={item}
      layout
      onClick={onClick}
      className="px-3 py-2.5 flex items-center justify-between gap-3 hover:bg-gray-100 dark:hover:bg-gray-750 cursor-pointer rounded-md"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="shrink-0">{icon}</span>
        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{label}</span>
        {description && (
          <span className="text-xs text-gray-400 dark:text-gray-500 truncate">{description}</span>
        )}
      </div>
      {end && (
        <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 shrink-0">{end}</span>
      )}
    </motion.li>
  );
}

export default function CommandSearchBar() {
  const {
    searchQuery, searchResults, isSearching, handleSearch, clearSearch,
    navigateToResult, searchHistory, clearSearchHistory, setSearchQuery,
  } = useSearch();

  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const sendIconRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && isFocused) {
        setIsFocused(false);
        e.currentTarget?.blur?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isFocused]);

  const handleChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    handleSearch(q);
  };

  const handleResultClick = (result) => {
    navigateToResult(result);
    setIsFocused(false);
  };

  const handleHistoryClick = (historyItem) => {
    if (typeof historyItem === 'object' && historyItem.tipo) {
      navigateToResult(historyItem);
    } else {
      const q = typeof historyItem === 'string' ? historyItem : historyItem.titulo;
      setSearchQuery(q);
      handleSearch(q);
      return;
    }
    setIsFocused(false);
  };

  const showEmptyState = searchQuery.length > 0 && !isSearching && searchResults.length === 0;
  const showHistory = searchQuery.length === 0 && searchHistory.length > 0;
  const showResults = searchResults.length > 0;

  const handleShowHistory = () => {
    inputRef.current?.focus();
    setIsFocused(true);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div
        className="relative"
        onMouseEnter={() => sendIconRef.current?.startAnimation()}
        onMouseLeave={() => sendIconRef.current?.stopAnimation()}
      >
        {/* Envuelto en un div que centra con flex (no con translate): el icono
            anima su propio transform al hover, y eso pisaba el -translate-y-1/2
            de Tailwind -- quedaba pegado abajo, fuera del cuadro. */}
        <div className="absolute left-3 inset-y-0 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </div>
        <input
          ref={inputRef}
          type="text"
          placeholder="Buscar activos, usuarios, tickets..."
          value={searchQuery}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          className="block w-full pl-9 pr-9 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4">
          <AnimatePresence mode="popLayout">
            {isSearching ? (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
              </motion.div>
            ) : searchQuery.length > 0 ? (
              <motion.button
                key="clear"
                type="button"
                onClick={clearSearch}
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 10, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </motion.button>
            ) : (
              <motion.button
                key="send"
                type="button"
                title="Ver historial de búsquedas"
                onClick={handleShowHistory}
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 10, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="text-gray-300 hover:text-gray-500 dark:hover:text-gray-400"
              >
                <Send ref={sendIconRef} className="w-4 h-4" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      <AnimatePresence>
        {isFocused && (showResults || showHistory || showEmptyState) && (
          <motion.div
            className="absolute z-50 w-full mt-1.5 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden bg-white dark:bg-gray-800"
            variants={container}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            {showResults && (
              <motion.ul className="p-1.5 max-h-96 overflow-y-auto">
                {searchResults.map((result) => (
                  <ResultRow
                    key={`${result.tipo}-${result.id}`}
                    icon={TYPE_ICON[result.tipo] || <Search className="h-4 w-4 text-gray-400" />}
                    label={result.titulo}
                    description={result.subtitulo}
                    end={TYPE_LABEL[result.tipo] || result.estado}
                    onClick={() => handleResultClick(result)}
                  />
                ))}
              </motion.ul>
            )}

            {showEmptyState && (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                No se encontraron resultados
              </div>
            )}

            {showHistory && (
              <div>
                <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100 dark:border-gray-700">
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Historial de búsquedas
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); clearSearchHistory(); }}
                    className="text-[11px] text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Limpiar
                  </button>
                </div>
                <motion.ul className="p-1.5">
                  {searchHistory.map((historyItem, index) => {
                    const isObject = typeof historyItem === 'object';
                    const displayText = isObject ? historyItem.titulo : historyItem;
                    return (
                      <ResultRow
                        key={index}
                        icon={isObject ? (TYPE_ICON[historyItem.tipo] || <Search className="h-4 w-4 text-gray-400" />) : <Search className="h-4 w-4 text-gray-400" />}
                        label={displayText}
                        description={isObject ? `${TYPE_LABEL[historyItem.tipo] || historyItem.tipo} visitado` : 'Búsqueda anterior'}
                        onClick={() => handleHistoryClick(historyItem)}
                      />
                    );
                  })}
                </motion.ul>
              </div>
            )}

            <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
                <span>{searchResults.length > 0 ? `${searchResults.length} resultado${searchResults.length === 1 ? '' : 's'}` : 'Escribe para buscar'}</span>
                <span>ESC para cerrar</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
